import type { ClassSession, Schedule, TemporarySchedule } from '../shared.js';

const WEEKDAY: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6
};

interface LocalClock {
  weekday: number;
  hours: number;
  minutes: number;
}

function localClock(date: Date, timezone: string): LocalClock | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date);
    const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value;
    const weekday = WEEKDAY[value('weekday') ?? ''];
    const hours = Number(value('hour'));
    const minutes = Number(value('minute'));
    if (weekday === undefined || !Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return { weekday, hours, minutes };
  } catch {
    return null;
  }
}

function timeToMinutes(value: string): number {
  const [hours = '0', minutes = '0'] = value.split(':');
  return Number(hours) * 60 + Number(minutes);
}

export function getScheduleHighlights(sessions: ClassSession[], currentMinutes: number): { currentIds: string[]; upcomingId: string | null } {
  const ordered = [...sessions].filter((session) => session.active).sort((left, right) => left.startTime.localeCompare(right.startTime));
  const currentIds = ordered.filter((session) => currentMinutes >= timeToMinutes(session.startTime) && currentMinutes < timeToMinutes(session.endTime)).map((session) => session.id);
  const upcomingId = ordered.find((session) => timeToMinutes(session.startTime) > currentMinutes)?.id ?? null;
  return { currentIds, upcomingId };
}

export function isScheduleEligible(schedule: Schedule | null, at = new Date()): boolean {
  if (!schedule) return true;
  const timestamp = at.getTime();
  if (schedule.startDate && timestamp < new Date(schedule.startDate).getTime()) return false;
  if (schedule.endDate && timestamp > new Date(schedule.endDate).getTime()) return false;

  const hasDays = schedule.daysOfWeek.length > 0;
  const hasTimeWindow = Boolean(schedule.startTime && schedule.endTime);
  if (!hasDays && !hasTimeWindow) return true;

  const clock = localClock(at, schedule.timezone);
  if (!clock) return false;
  const currentMinutes = clock.hours * 60 + clock.minutes;
  const isAllowedDay = (day: number) => !hasDays || schedule.daysOfWeek.includes(day);

  if (!hasTimeWindow) return isAllowedDay(clock.weekday);

  const startMinutes = timeToMinutes(schedule.startTime!);
  const endMinutes = timeToMinutes(schedule.endTime!);
  if (startMinutes <= endMinutes) {
    return isAllowedDay(clock.weekday) && currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }
  if (currentMinutes >= startMinutes) return isAllowedDay(clock.weekday);
  return currentMinutes <= endMinutes && isAllowedDay((clock.weekday + 6) % 7);
}

/** Date ranges replace the whole weekly schedule, including intentionally empty days. */
export function resolveClassSchedule(regular: ClassSession[], temporary: TemporarySchedule[] = [], at = new Date()): { sessions: ClassSession[]; temporary?: TemporarySchedule } {
  const date = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
  const override = temporary.find((item) => item.startDate <= date && date <= item.endDate);
  const sessions = (override?.sessions ?? regular).filter((session) => !/^strength\s*area$/i.test(session.title.trim()));
  return { sessions, temporary: override };
}
